import {OrderUtils} from "./../../helpers/orderUtils";
import {makeAutoObservable} from "mobx";
import {ValidatedValue} from "../../helpers/validatedValue";
import {OrderDetailModel, OrderStatusEnum} from "../models/order";
import {CargoType} from "../models/cargo";
import {RelationType} from "../models/relation";
import {ContainerSizeType} from "../models/container";
import {DangerousGoodInStore, IDangerousGoodInStore} from "./dangerousGoodInStore";
import Validator from "../../helpers/validator";
import {errorMessages} from "../../helpers/errorMessage";
import i18n from "../../i18n/i18n";
import {isDangerousGood} from "../../helpers/dangerousGoods";
import {ValidatorModel} from "../../common/models/validator";
import {PaymentMethod} from "../models/transport";
import {DateUtils} from "../../helpers/dateUtils";
import {CompanyModelForList} from "../../dictionaries/models/company";

export interface IOrderStore {
    clearStore: () => void;

    status: ValidatedValue<OrderStatusEnum | "">;
    setStatus: (status: OrderStatusEnum | "") => void;
    relationType: ValidatedValue<RelationType | "">;
    setRelationType: (cargoType: RelationType | "") => void;
    cargoType: ValidatedValue<CargoType | "">;
    setCargoType: (cargoType: CargoType | "") => void;
    pinCode: ValidatedValue<string>;
    setPinCode: (pinCode: string) => void;
    barcodeNo: ValidatedValue<string>;
    setBarcodeNo: (barcodeNo: string) => void;

    requestedTemp1: ValidatedValue<number | null>;
    setRequestedTemp1: (requestedTemp1: number) => void;
    requestedTemp2: ValidatedValue<number | null>;
    setRequestedTemp2: (requestedTemp2: number) => void;

    trailerRegNo: ValidatedValue<string>;
    setTrailerRegNo: (trailerRegNo: string) => void;
    transportCompanyId: ValidatedValue<string>;
    setTransportCompanyId: (transportCompanyId: string) => void;
    recipient: ValidatedValue<string>;
    setRecipient: (recipient: string) => void;
    lorryRegNo: ValidatedValue<string>;
    supplierLorryRegNo: ValidatedValue<string>;
    setSupplierLorryRegNo: (supplierLorryRegNo: string) => void;
    recipientLorryRegNo: ValidatedValue<string>;
    setRecipientLorryRegNo: (recipientLorryRegNo: string) => void;

    shipownerId: ValidatedValue<string>;
    setShipownerId: (shipownerId: string) => void;
    shipownerName: ValidatedValue<string>;
    setShipownerName: (shipownerName: string) => void;
    voyageNo: ValidatedValue<string>;
    setVoyageNo: (voyageNo: string) => void;
    arrivalDepartureTime: ValidatedValue<Date | null | string>;
    setArrivalDepartureTime: (arrivalDepartureTime: Date | null | string) => void;
    ferryId: ValidatedValue<string>;
    setFerryId: (ferryId: string) => void;
    originDestinationPortId: ValidatedValue<string>;
    setOriginDestinationPortId: (originDestinationPortId: string) => void;
    originDestinationCountry: ValidatedValue<string>;
    setOriginDestinationCountry: (originDestinationCountry: string) => void;
    originDestinationCountryName: ValidatedValue<string>;
    setOriginDestinationCountryName: (originDestinationCountryName: string) => void;
    bookingNo: ValidatedValue<string>;
    setBookingNo: (bookingNo: string) => void;

    cargoDesc: ValidatedValue<string>;
    setCargoDesc: (cargoDesc: string) => void;
    weight: ValidatedValue<number | null>;
    setWeight: (weight: number) => void;
    length: ValidatedValue<number | null>;
    setLength: (length: number | null) => void;
    height: ValidatedValue<number | null>;
    setHeight: (height: number | null) => void;
    width: ValidatedValue<number | null>;
    setWidth: (width: number | null) => void;
    packingQuantity: ValidatedValue<number | null>;
    setPackingQuantity: (packingQuantity: number) => void;
    cbm: ValidatedValue<number | null>;
    setCbm: (cbm: number | null) => void;
    packingId: ValidatedValue<string>;
    setPackingId: (packingId: string) => void;
    packingName: ValidatedValue<string>;
    setPackingName: (packingName: string) => void;
    remarks: ValidatedValue<string>;
    setRemarks: (remarks: string) => void;
    wagonRegNo: ValidatedValue<string>;
    setWagonRegNo: (wagonRegNo: string) => void;
    supplierWagonRegNo: ValidatedValue<string>;
    setSupplierWagonRegNo: (wagonRegNo: string) => void;
    recipientWagonRegNo: ValidatedValue<string>;
    setRecipientWagonRegNo: (recipientWagonRegNo: string) => void;
    securingDeclaration: ValidatedValue<boolean>;
    setSecuringDeclaration: (securingDeclaration: boolean) => void;

    containerNo: ValidatedValue<string>;
    setContainerNo: (containerNo: string) => void;
    containerSize: ValidatedValue<ContainerSizeType | "">;
    setContainerSize: (containerSize: ContainerSizeType | "") => void;
    vgm: ValidatedValue<number | null>;
    setVgm: (vgm: number) => void;

    paymentMethod: ValidatedValue<PaymentMethod | "">;
    setPaymentMethod: (paymentMethod: PaymentMethod | "") => void;
    paymentDocumentNumber: ValidatedValue<string>;
    setPaymentDocumentNumber: (paymentDocumentNumber: string) => void;
    storageTime: ValidatedValue<number | null>;
    setStorageTime: (storageTime: number | null) => void;

    createDangerousGood: () => void;
    removeDangerousGood: (dangerousGoodId: number) => void;
    dangerousGoods: IDangerousGoodInStore[];

    addDangerousGoodsValidation: () => void;

    orderIsValid: () => boolean;

    displayPinCode: boolean;
    displayBarCode: boolean;
    displayLorryRegNo: boolean;
    displaySupplierLorryRegNo: boolean;
    displayRecipientLorryRegNo: boolean;
    displayTrailerRegNo: boolean;
    displaySecuringDeclaration: boolean;
    displayRecipient: boolean;
    displayCargoProperties: boolean;
    displayWagonRegNo: boolean;
    displaySupplierWagonRegNo: boolean;
    displayRecipientWagonRegNo: boolean;
    displayContainerProperties: boolean;
    displayTemperatures: boolean;
    displayVoyageSection: boolean;
    displayCostsSection: boolean;

    companies: CompanyModelForList[];
    setCompanies: (companies: CompanyModelForList[]) => void;

    statusesForRelationType: OrderStatusEnum[];
    setStatusesForRelationType: (statuses: OrderStatusEnum[]) => void;

    setPaymentMethodForCompany: (selectedTransportCompanyId: string) => void;

    setOrder: (orderData: OrderDetailModel | null | undefined) => void;
}

export class OrderStore implements IOrderStore {
    _status: ValidatedValue<OrderStatusEnum | "">;
    _relationType: ValidatedValue<RelationType | "">;
    _cargoType: ValidatedValue<CargoType | "">;
    _pinCode: ValidatedValue<string>;
    _barcodeNo: ValidatedValue<string>;

    _requestedTemp1: ValidatedValue<number | null>;
    _requestedTemp2: ValidatedValue<number | null>;

    _trailerRegNo: ValidatedValue<string>;
    _transportCompanyId: ValidatedValue<string>;
    _recipient: ValidatedValue<string>;
    _lorryRegNo: ValidatedValue<string>;
    _supplierLorryRegNo: ValidatedValue<string>;
    _recipientLorryRegNo: ValidatedValue<string>;

    _shipownerId: ValidatedValue<string>;
    _shipownerName: ValidatedValue<string>;
    _voyageNo: ValidatedValue<string>;
    _arrivalDepartureTime: ValidatedValue<Date | null | string>;
    _ferryId: ValidatedValue<string>;
    _originDestinationPortId: ValidatedValue<string>;
    _originDestinationCountry: ValidatedValue<string>;
    _originDestinationCountryName: ValidatedValue<string>;
    _bookingNo: ValidatedValue<string>;

    _cargoDesc: ValidatedValue<string>;
    _weight: ValidatedValue<number | null>;
    _length: ValidatedValue<number | null>;
    _height: ValidatedValue<number | null>;
    _width: ValidatedValue<number | null>;
    _packingQuantity: ValidatedValue<number | null>;
    _cbm: ValidatedValue<number | null>;
    _packingId: ValidatedValue<string>;
    _packingName: ValidatedValue<string>;
    _remarks: ValidatedValue<string>;
    _wagonRegNo: ValidatedValue<string>;
    _supplierWagonRegNo: ValidatedValue<string>;
    _recipientWagonRegNo: ValidatedValue<string>;
    _securingDeclaration: ValidatedValue<boolean>;

    _containerNo: ValidatedValue<string>;
    _containerSize: ValidatedValue<ContainerSizeType | "">;
    _vgm: ValidatedValue<number | null>;

    _paymentMethod: ValidatedValue<PaymentMethod | "">;
    _paymentDocumentNumber: ValidatedValue<string>;
    _storageTime: ValidatedValue<number | null>;

    _dangerousGoods: IDangerousGoodInStore[];

    _companies: CompanyModelForList[];

    _statusesForRelationType: OrderStatusEnum[];

    constructor() {
        makeAutoObservable(this);

        const requiredValidator = {
            name: "valueIsNotEmpty",
            fn: Validator.isNotEmpty,
            errorMessage: errorMessages.required
        };

        const isOnlyPositiveNumberOrEmptyValidator = {
            name: "valueIsPositiveNumberOrEmpty",
            fn: Validator.isOnlyPositiveNumberOrEmpty,
            errorMessage: () => i18n.t("errorMessages.onlyNumber")
        };

        const isOnlyPositiveNumberWithCommaOrEmpty = {
            name: "valueIsPositiveNumberWithCommaOrEmpty",
            fn: Validator.isOnlyPositiveNumberWithCommaOrEmpty,
            errorMessage: () => i18n.t("errorMessages.onlyNumber")
        };

        const isCorrectTemperatureValidators = {
            name: "valueIsCorrectTemperature",
            fn: Validator.isCorrectNumberWithCommaOrEmpty,
            errorMessage: () => i18n.t("errorMessages.correctTemperature")
        };

        const isCorrectBarcodeValidators = {
            name: "valueIsCorrectBarcode",
            fn: () => Validator.maxLength(this.barcodeNo.value, 20),
            errorMessage: () => i18n.t("transportation.order.addOrder.errors.barcodeErrorMessage")
        };

        const correctMaxRegNoLength = 10;
        const correctMaxContainerNoLength = 11;
        const correctMaxWagonRegNoLength = 14;
        const correctMaxBookingNoLength = 20;
        const correctMaxVoyageNoLength = 15;
        const correctMaxCargoDescAndRemarksLength = 512;
        const correctMaxRecipientLength = 100;

        const trailerRegNoLengthValidator = {
            name: "trailerRegNoLengthValidator",
            fn: () => Validator.maxLength(this.trailerRegNo.value, correctMaxRegNoLength),
            errorMessage: () => i18n.t("transportation.errorMessage.noLengthValidator", {object: i18n.t("transportation.errorMessage.trailerRegNo"), number: correctMaxRegNoLength})
        };

        const lorryRegNoLengthValidator = {
            name: "lorryRegNoLengthValidator",
            fn: () => Validator.maxLength(this.lorryRegNo.value, correctMaxRegNoLength),
            errorMessage: () => i18n.t("transportation.errorMessage.noLengthValidator", {object: i18n.t("transportation.errorMessage.lorryRegNo"), number: correctMaxRegNoLength})
        };

        const supplierLorryRegNoLengthValidator = {
            name: "supplierLorryRegNoLengthValidator",
            fn: () => Validator.maxLength(this.supplierLorryRegNo.value, correctMaxRegNoLength),
            errorMessage: () => i18n.t("transportation.errorMessage.noLengthValidator", {object: i18n.t("transportation.errorMessage.lorryRegNo"), number: correctMaxRegNoLength})
        };

        const recipientLorryRegNoLengthValidator = {
            name: "recipientLorryRegNoLengthValidator",
            fn: () => Validator.maxLength(this.recipientLorryRegNo.value, correctMaxRegNoLength),
            errorMessage: () => i18n.t("transportation.errorMessage.noLengthValidator", {object: i18n.t("transportation.errorMessage.lorryRegNo"), number: correctMaxRegNoLength})
        };

        const containerNoLengthValidator = {
            name: "containerNoLengthValidator",
            fn: () => Validator.maxLength(this.containerNo.value, correctMaxContainerNoLength),
            errorMessage: () => i18n.t("transportation.errorMessage.noLengthValidator", {object: i18n.t("transportation.errorMessage.containerNo"), number: correctMaxContainerNoLength})
        };

        const wagonRegNoLengthValidator = {
            name: "wagonRegNoLengthValidator",
            fn: () => Validator.maxLength(this.wagonRegNo.value, correctMaxWagonRegNoLength),
            errorMessage: () => i18n.t("transportation.errorMessage.noLengthValidator", {object: i18n.t("transportation.errorMessage.wagonRegNo"), number: correctMaxWagonRegNoLength})
        };

        const supplierWagonRegNoLengthValidator = {
            name: "supplierWagonRegNoLengthValidator",
            fn: () => Validator.maxLength(this.supplierWagonRegNo.value, correctMaxWagonRegNoLength),
            errorMessage: () => i18n.t("transportation.errorMessage.noLengthValidator", {object: i18n.t("transportation.errorMessage.wagonRegNo"), number: correctMaxWagonRegNoLength})
        };

        const recipientWagonRegNoLengthValidator = {
            name: "recipientWagonRegNoLengthValidator",
            fn: () => Validator.maxLength(this.recipientWagonRegNo.value, correctMaxWagonRegNoLength),
            errorMessage: () => i18n.t("transportation.errorMessage.noLengthValidator", {object: i18n.t("transportation.errorMessage.wagonRegNo"), number: correctMaxWagonRegNoLength})
        };

        const bookingNoLengthValidator = {
            name: "bookingNoLengthValidator",
            fn: () => Validator.maxLength(this.bookingNo.value, correctMaxBookingNoLength),
            errorMessage: () => i18n.t("transportation.errorMessage.noLengthValidator", {object: i18n.t("transportation.errorMessage.bookingNo"), number: correctMaxBookingNoLength})
        };

        const voyageNoLengthValidator = {
            name: "voyageNoLengthValidator",
            fn: () => Validator.maxLength(this.voyageNo.value, correctMaxVoyageNoLength),
            errorMessage: () => i18n.t("transportation.errorMessage.noLengthValidator", {object: i18n.t("transportation.errorMessage.voyageNo"), number: correctMaxVoyageNoLength})
        };

        const cargoDescLengthValidator = {
            name: "cargoDescLengthValidator",
            fn: () => Validator.maxLength(this.cargoDesc.value, correctMaxCargoDescAndRemarksLength),
            errorMessage: () => i18n.t("transportation.errorMessage.lengthValidator", {firstPart: i18n.t("transportation.errorMessage.cargoDesc"), number: correctMaxCargoDescAndRemarksLength})
        };

        const remarksLengthValidator = {
            name: "remarksLengthValidator",
            fn: () => Validator.maxLength(this.remarks.value, correctMaxCargoDescAndRemarksLength),
            errorMessage: () => i18n.t("transportation.errorMessage.lengthValidator", {firstPart: i18n.t("transportation.errorMessage.remarks"), number: correctMaxCargoDescAndRemarksLength})
        };

        const recipientLengthValidator = {
            name: "recipientLengthValidator",
            fn: () => Validator.maxLength(this.recipient.value, correctMaxRecipientLength),
            errorMessage: () => i18n.t("transportation.errorMessage.recipientLengthValidator", {number: correctMaxRecipientLength})
        };

        this._status = new ValidatedValue(OrderStatusEnum.REGISTERED, [requiredValidator]);
        this._relationType = new ValidatedValue("", [requiredValidator]);
        this._cargoType = new ValidatedValue("", [requiredValidator]);
        this._pinCode = new ValidatedValue("", [this.pinCodeValidator]);
        this._barcodeNo = new ValidatedValue("", [isCorrectBarcodeValidators]);

        this._requestedTemp1 = new ValidatedValue(null, [isCorrectTemperatureValidators]);
        this._requestedTemp2 = new ValidatedValue(null, [isCorrectTemperatureValidators]);

        this._trailerRegNo = new ValidatedValue("", [this.trailerRegNoValidator, trailerRegNoLengthValidator]);
        this._transportCompanyId = new ValidatedValue("", [this.transportCompanyValidator]);
        this._recipient = new ValidatedValue("", [this.recipientValidator, recipientLengthValidator]);
        this._lorryRegNo = new ValidatedValue("", [this.lorryRegNoValidator, lorryRegNoLengthValidator]);
        this._supplierLorryRegNo = new ValidatedValue("", [this.supplierLorryRegNoValidator, supplierLorryRegNoLengthValidator]);
        this._recipientLorryRegNo = new ValidatedValue("", [this.recipientLorryRegNoValidator, recipientLorryRegNoLengthValidator]);

        this._shipownerId = new ValidatedValue("");
        this._shipownerName = new ValidatedValue("");
        this._voyageNo = new ValidatedValue("", [this.voyageSectionRequiredValidator, voyageNoLengthValidator]);
        this._arrivalDepartureTime = new ValidatedValue(null, [this.voyageSectionRequiredValidator, this.dateWithTimeValidator]);
        this._ferryId = new ValidatedValue("", [this.voyageSectionRequiredValidator]);
        this._originDestinationPortId = new ValidatedValue("", [this.voyageSectionRequiredValidator]);
        this._originDestinationCountry = new ValidatedValue("");
        this._originDestinationCountryName = new ValidatedValue("");
        this._bookingNo = new ValidatedValue("", [this.voyageSectionRequiredValidator, bookingNoLengthValidator]);

        this._cargoDesc = new ValidatedValue("", [this.cargoDescValidator, cargoDescLengthValidator]);
        this._weight = new ValidatedValue(null, [requiredValidator, isOnlyPositiveNumberWithCommaOrEmpty]);
        this._length = new ValidatedValue(null, [this.cargoDimensionsValidator, isOnlyPositiveNumberWithCommaOrEmpty]);
        this._height = new ValidatedValue(null, [this.cargoDimensionsValidator, isOnlyPositiveNumberWithCommaOrEmpty]);
        this._width = new ValidatedValue(null, [this.cargoDimensionsValidator, isOnlyPositiveNumberWithCommaOrEmpty]);
        this._packingQuantity = new ValidatedValue(null, [isOnlyPositiveNumberOrEmptyValidator]);
        this._cbm = new ValidatedValue(null, [isOnlyPositiveNumberWithCommaOrEmpty]);
        this._packingId = new ValidatedValue("");
        this._packingName = new ValidatedValue("");
        this._remarks = new ValidatedValue("", [remarksLengthValidator]);
        this._wagonRegNo = new ValidatedValue("", [this.wagonRegNoValidator, wagonRegNoLengthValidator]);
        this._supplierWagonRegNo = new ValidatedValue("", [supplierWagonRegNoLengthValidator]);
        this._recipientWagonRegNo = new ValidatedValue("", [recipientWagonRegNoLengthValidator]);
        this._securingDeclaration = new ValidatedValue(false);

        this._containerNo = new ValidatedValue("", [this.containerValidator, containerNoLengthValidator]);
        this._containerSize = new ValidatedValue("", [this.containerValidator]);
        this._vgm = new ValidatedValue(null, [this.containerValidator, isOnlyPositiveNumberWithCommaOrEmpty]);

        this._paymentMethod = new ValidatedValue("");
        this._paymentDocumentNumber = new ValidatedValue("");
        this._storageTime = new ValidatedValue(null);

        this._dangerousGoods = [];
        this.createDangerousGood();

        this._companies = [];

        this._statusesForRelationType = [];
    }

    clearStore() {
        this._status.clearValidation();
        this._relationType.clearValidation();
        this._cargoType.clearValidation();
        this._pinCode.clearValidation();
        this._barcodeNo.clearValidation();

        this._requestedTemp1.clearValidation();
        this._requestedTemp2.clearValidation();

        this._trailerRegNo.clearValidation();
        this._transportCompanyId.clearValidation();
        this._recipient.clearValidation();
        this._lorryRegNo.clearValidation();
        this._supplierLorryRegNo.clearValidation();
        this._recipientLorryRegNo.clearValidation();

        this._shipownerId.clearValidation();
        this._shipownerName.clearValidation();
        this._voyageNo.clearValidation();
        this._arrivalDepartureTime.clearValidation();
        this._ferryId.clearValidation();
        this._originDestinationPortId.clearValidation();
        this._originDestinationCountry.clearValidation();
        this._originDestinationCountryName.clearValidation();
        this._bookingNo.clearValidation();

        this._cargoDesc.clearValidation();
        this._weight.clearValidation();
        this._length.clearValidation();
        this._height.clearValidation();
        this._width.clearValidation();
        this._packingQuantity.clearValidation();
        this._cbm.clearValidation();
        this._packingId.clearValidation();
        this._packingName.clearValidation();
        this._remarks.clearValidation();
        this._wagonRegNo.clearValidation();
        this._supplierWagonRegNo.clearValidation();
        this._recipientWagonRegNo.clearValidation();
        this._securingDeclaration.clearValidation();

        this._containerNo.clearValidation();
        this._containerSize.clearValidation();
        this._vgm.clearValidation();

        this._paymentMethod.clearValidation();
        this._paymentDocumentNumber.clearValidation();
        this._storageTime.clearValidation();

        this._status.setValue(OrderStatusEnum.REGISTERED);
        this._relationType.setValue("");
        this._cargoType.setValue("");
        this._pinCode.setValue("");
        this._barcodeNo.setValue("");

        this._requestedTemp1.setValue(null);
        this._requestedTemp2.setValue(null);

        this._trailerRegNo.setValue("");
        this._transportCompanyId.setValue("");
        this._recipient.setValue("");
        this._lorryRegNo.setValue("");
        this._supplierLorryRegNo.setValue("");
        this._recipientLorryRegNo.setValue("");

        this._shipownerId.setValue("");
        this._shipownerName.setValue("");
        this._voyageNo.setValue("");
        this._arrivalDepartureTime.setValue(null);
        this._ferryId.setValue("");
        this._originDestinationPortId.setValue("");
        this._originDestinationCountry.setValue("");
        this._originDestinationCountryName.setValue("");
        this._bookingNo.setValue("");

        this._cargoDesc.setValue("");
        this._weight.setValue(null);
        this._length.setValue(null);
        this._height.setValue(null);
        this._width.setValue(null);
        this._packingQuantity.setValue(null);
        this._cbm.setValue(null);
        this._packingId.setValue("");
        this._packingName.setValue("");
        this._remarks.setValue("");
        this._wagonRegNo.setValue("");
        this._supplierWagonRegNo.setValue("");
        this._recipientWagonRegNo.setValue("");
        this._securingDeclaration.setValue(false);

        this._containerNo.setValue("");
        this._containerSize.setValue("");
        this._vgm.setValue(null);

        this._paymentMethod.setValue("");
        this._paymentDocumentNumber.setValue("");
        this._storageTime.setValue(null);

        this._dangerousGoods = [];
        this.createDangerousGood();

        this._companies = [];

        this._statusesForRelationType = [];
    }

    get status() {
        return this._status;
    }

    setStatus(status: OrderStatusEnum | "") {
        this._status.setValue(status);
    }

    get relationType() {
        return this._relationType;
    }

    setRelationType(relationType: RelationType | "") {
        this._relationType.setValue(relationType);
    }

    get cargoType() {
        return this._cargoType;
    }

    setCargoType(cargoType: CargoType | "") {
        this._cargoType.setValue(cargoType);
    }

    get pinCode() {
        return this._pinCode;
    }

    setPinCode(pinCode: string) {
        this._pinCode.setValue(pinCode);
    }

    get barcodeNo() {
        return this._barcodeNo;
    }

    setBarcodeNo(barcodeNo: string) {
        this._barcodeNo.setValue(barcodeNo);
    }

    get requestedTemp1() {
        return this._requestedTemp1;
    }

    setRequestedTemp1(requestedTemp1: number | null) {
        this._requestedTemp1.setValue(requestedTemp1);
    }

    get requestedTemp2() {
        return this._requestedTemp2;
    }

    setRequestedTemp2(requestedTemp2: number | null) {
        this._requestedTemp2.setValue(requestedTemp2);
    }

    get trailerRegNo() {
        return this._trailerRegNo;
    }

    setTrailerRegNo(trailerRegNo: string) {
        this._trailerRegNo.setValue(trailerRegNo);
    }

    get transportCompanyId() {
        return this._transportCompanyId;
    }

    setTransportCompanyId(transportCompanyId: string) {
        this._transportCompanyId.setValue(transportCompanyId);
    }

    get recipient() {
        return this._recipient;
    }

    setRecipient(recipient: string) {
        this._recipient.setValue(recipient);
    }

    get lorryRegNo() {
        return this._lorryRegNo;
    }

    setLorryRegNo(lorryRegNo: string) {
        this._lorryRegNo.setValue(lorryRegNo);
    }

    get supplierLorryRegNo() {
        return this._supplierLorryRegNo;
    }

    setSupplierLorryRegNo(supplierLorryRegNo: string) {
        this._supplierLorryRegNo.setValue(supplierLorryRegNo);
    }

    get recipientLorryRegNo() {
        return this._recipientLorryRegNo;
    }

    setRecipientLorryRegNo(recipientLorryRegNo: string) {
        this._recipientLorryRegNo.setValue(recipientLorryRegNo);
    }

    get shipownerId() {
        return this._shipownerId;
    }

    setShipownerId(shipownerId: string) {
        this._shipownerId.setValue(shipownerId);
    }

    get shipownerName() {
        return this._shipownerName;
    }

    setShipownerName(shipownerName: string) {
        this._shipownerName.setValue(shipownerName);
    }

    get voyageNo() {
        return this._voyageNo;
    }

    setVoyageNo(voyageNo: string) {
        this._voyageNo.setValue(voyageNo);
    }

    get arrivalDepartureTime() {
        return this._arrivalDepartureTime;
    }

    setArrivalDepartureTime(arrivalDepartureTime: Date | null | string) {
        this._arrivalDepartureTime.setValue(arrivalDepartureTime);
    }

    get ferryId() {
        return this._ferryId;
    }

    setFerryId(ferryId: string) {
        this._ferryId.setValue(ferryId);
    }

    get originDestinationPortId() {
        return this._originDestinationPortId;
    }

    setOriginDestinationPortId(originDestinationPortId: string) {
        this._originDestinationPortId.setValue(originDestinationPortId);
    }

    get originDestinationCountry() {
        return this._originDestinationCountry;
    }

    setOriginDestinationCountry(originDestinationCountry: string) {
        this._originDestinationCountry.setValue(originDestinationCountry);
    }

    get originDestinationCountryName() {
        return this._originDestinationCountryName;
    }

    setOriginDestinationCountryName(originDestinationCountryName: string) {
        this._originDestinationCountryName.setValue(originDestinationCountryName);
    }

    get bookingNo() {
        return this._bookingNo;
    }

    setBookingNo(bookingNo: string) {
        this._bookingNo.setValue(bookingNo);
    }

    get cargoDesc() {
        return this._cargoDesc;
    }

    setCargoDesc(cargoDesc: string) {
        this._cargoDesc.setValue(cargoDesc);
    }

    get weight() {
        return this._weight;
    }

    setWeight(weight: number | null) {
        this._weight.setValue(weight);
    }

    get length() {
        return this._length;
    }

    setLength(length: number | null) {
        this._length.setValue(length);
    }

    get height() {
        return this._height;
    }

    setHeight(height: number | null) {
        this._height.setValue(height);
    }

    get width() {
        return this._width;
    }

    setWidth(width: number | null) {
        this._width.setValue(width);
    }

    get packingQuantity() {
        return this._packingQuantity;
    }

    setPackingQuantity(packingQuantity: number | null) {
        this._packingQuantity.setValue(packingQuantity);
    }

    get cbm() {
        return this._cbm;
    }

    setCbm(cbm: number | null) {
        this._cbm.setValue(cbm);
    }

    get packingId() {
        return this._packingId;
    }

    setPackingId(packingId: string) {
        this._packingId.setValue(packingId);
    }

    get packingName() {
        return this._packingName;
    }

    setPackingName(packingName: string) {
        this._packingName.setValue(packingName);
    }

    get remarks() {
        return this._remarks;
    }

    setRemarks(remarks: string) {
        this._remarks.setValue(remarks);
    }

    get wagonRegNo() {
        return this._wagonRegNo;
    }

    setWagonRegNo(wagonRegNo: string) {
        this._wagonRegNo.setValue(wagonRegNo);
    }

    get supplierWagonRegNo() {
        return this._supplierWagonRegNo;
    }

    setSupplierWagonRegNo(wagonRegNo: string) {
        this._supplierWagonRegNo.setValue(wagonRegNo);
    }

    get recipientWagonRegNo() {
        return this._recipientWagonRegNo;
    }

    setRecipientWagonRegNo(recipientWagonRegNo: string) {
        this._recipientWagonRegNo.setValue(recipientWagonRegNo);
    }

    get securingDeclaration() {
        return this._securingDeclaration;
    }

    setSecuringDeclaration(securingDeclaration: boolean) {
        this._securingDeclaration.setValue(securingDeclaration);
    }

    get containerNo() {
        return this._containerNo;
    }

    setContainerNo(containerNo: string) {
        this._containerNo.setValue(containerNo);
    }

    get containerSize() {
        return this._containerSize;
    }

    setContainerSize(containerSize: ContainerSizeType | "") {
        this._containerSize.setValue(containerSize);
    }

    get vgm() {
        return this._vgm;
    }

    setVgm(vgm: number | null) {
        this._vgm.setValue(vgm);
    }

    get paymentMethod() {
        return this._paymentMethod;
    }

    setPaymentMethod(paymentMethod: PaymentMethod | "") {
        this._paymentMethod.setValue(paymentMethod);
    }

    get paymentDocumentNumber() {
        return this._paymentDocumentNumber;
    }

    setPaymentDocumentNumber(paymentDocumentNumber: string) {
        this._paymentDocumentNumber.setValue(paymentDocumentNumber);
    }

    get storageTime() {
        return this._storageTime;
    }

    setStorageTime(storageTime: number | null) {
        this._storageTime.setValue(storageTime);
    }

    get dangerousGoods() {
        return this._dangerousGoods;
    }

    createDangerousGood() {
        this._dangerousGoods.push(new DangerousGoodInStore());
    }

    removeDangerousGood(dangerousGoodId: number) {
        this._dangerousGoods.splice(dangerousGoodId, 1);
    }

    addDangerousGoodsValidation() {
        this.dangerousGoods.forEach((item) => {
            item.unCodeId.removeValidators();
            item.packingId.removeValidators();
            item.packingQuantity.removeValidators();
            item.packingGroup.removeValidators();
            item.weight.removeValidators();

            item.unCodeId.clearValidation();
            item.packingId.clearValidation();
            item.packingQuantity.clearValidation();
            item.packingGroup.clearValidation();
            item.weight.clearValidation();

            item.unCodeId.addValidators([this.requireDangerousGoodsValidator]);
            item.packingId.addValidators([this.requireDangerousGoodsValidator]);
            item.packingQuantity.addValidators([this.requireDangerousGoodsValidator]);
            item.packingGroup.addValidators([this.requireDangerousGoodsValidator]);
            item.weight.addValidators([this.requireDangerousGoodsValidator]);
        });
    }

    orderIsValid(): boolean {
        this._status.validate();
        this._relationType.validate();
        this._cargoType.validate();
        this._pinCode.validate();
        this._barcodeNo.validate();

        this._requestedTemp1.validate();
        this._requestedTemp2.validate();

        this._trailerRegNo.validate();
        this._transportCompanyId.validate();
        this._recipient.validate();
        this._lorryRegNo.validate();
        this._supplierLorryRegNo.validate();
        this._recipientLorryRegNo.validate();

        this._shipownerId.validate();
        this._voyageNo.validate();
        this._arrivalDepartureTime.validate();
        this._ferryId.validate();
        this._originDestinationPortId.validate();
        this._originDestinationCountry.validate();
        this._bookingNo.validate();

        this._cargoDesc.validate();
        this._weight.validate();
        this._length.validate();
        this._height.validate();
        this._width.validate();
        this._packingQuantity.validate();
        this._cbm.validate();
        this._packingId.validate();
        this._packingName.validate();
        this._remarks.validate();
        this._wagonRegNo.validate();
        this._supplierWagonRegNo.validate();
        this._recipientWagonRegNo.validate();
        this._securingDeclaration.validate();

        this._containerNo.validate();
        this._containerSize.validate();
        this._vgm.validate();

        this._paymentMethod.validate();
        this._paymentDocumentNumber.validate();

        let dangerousGoodsAreValid = true;
        const dangerousGoods = this._dangerousGoods;
        dangerousGoods.map((dangerousGood) => {
            if (!this.dangerousGoodsAreValid(dangerousGood)) {
                dangerousGoodsAreValid = false;
            }
        });

        return this._status.isValid && this._relationType.isValid && this._cargoType.isValid &&
            this._pinCode.isValid && this._barcodeNo.isValid &&

            this._requestedTemp1.isValid && this._requestedTemp2.isValid &&

            this._trailerRegNo.isValid && this._transportCompanyId.isValid && this._recipient.isValid &&
            this._lorryRegNo.isValid && this._supplierLorryRegNo.isValid && this._recipientLorryRegNo.isValid &&

            this._shipownerId.isValid && this._voyageNo.isValid &&
            this._arrivalDepartureTime.isValid && this._ferryId.isValid &&
            this._originDestinationPortId.isValid && this._originDestinationCountry.isValid &&
            this._bookingNo.isValid &&

            this._cargoDesc.isValid && this._weight.isValid &&
            this._length.isValid && this._height.isValid && this._width.isValid &&
            this._packingQuantity.isValid && this._cbm.isValid && this._packingId.isValid && this._packingName.isValid &&
            this._remarks.isValid && this._wagonRegNo.isValid && this._supplierWagonRegNo.isValid &&
            this._securingDeclaration.isValid && this._recipientWagonRegNo.isValid &&

            this._containerNo.isValid && this._containerSize.isValid && this._vgm.isValid &&

            this._paymentMethod.isValid && this._paymentDocumentNumber.isValid &&

            dangerousGoodsAreValid;
    }

    dangerousGoodsAreValid(dangerousGood: IDangerousGoodInStore): boolean {
        dangerousGood.unCodeId.validate();
        dangerousGood.packingId.validate();
        dangerousGood.packingGroup.validate();
        dangerousGood.packingQuantity.validate();
        dangerousGood.weight.validate();

        return dangerousGood.unCodeId.isValid && dangerousGood.packingId.isValid && dangerousGood.packingGroup.isValid &&
            dangerousGood.packingQuantity.isValid && dangerousGood.weight.isValid;
    }


    get transportCompanyValidator(): ValidatorModel {
        return {
            name: "transportCompanyValidator",
            fn: (value: any) => {
                const relationType = this._relationType.value;

                if (relationType && relationType !== RelationType.RAIL_FERRY && relationType !== RelationType.FERRY_RAIL) {
                    return Validator.isNotEmpty(value);
                } else {
                    return true;
                }
            },
            errorMessage: errorMessages.required
        };
    }

    get trailerRegNoValidator(): ValidatorModel {
        return {
            name: "trailerRegNoValidator",
            fn: (value: any) => {
                const relationType = this._relationType.value;
                const cargoType = this._cargoType.value;

                switch (relationType) {
                    case RelationType.FERRY_DEPO:
                    case RelationType.ROAD_DEPO_FERRY:
                    case RelationType.FERRY_DEPO_ROAD:
                    case RelationType.ROAD_DEPO_ROAD:
                    case RelationType.ROAD_DEPO_RAIL:
                    case RelationType.ROAD_DEPO:
                    case RelationType.RAIL_DEPO_ROAD:
                        if (cargoType === CargoType.NORMATIVE_GENERAL_CARGO || cargoType === CargoType.DANGEROUS_GENERAL_CARGO) {
                            return true;
                        } else {
                            return Validator.isNotEmpty(value);
                        }
                    case RelationType.RAIL_DEPO_FERRY:
                    case RelationType.FERRY_DEPO_RAIL:
                        return Validator.isNotEmpty(value);
                    case RelationType.RAIL_DEPO:
                    case RelationType.RAIL_DEPO_RAIL:
                        if (cargoType === CargoType.NORMATIVE_TRAILER || cargoType === CargoType.DANGEROUS_TRAILER) {
                            return Validator.isNotEmpty(value);
                        } else {
                            return true;
                        }
                    case RelationType.RAIL_FERRY:
                    case RelationType.FERRY_RAIL:
                        return true;
                    default:
                        return true;
                }
            },
            errorMessage: errorMessages.required
        };
    }

    get lorryRegNoValidator(): ValidatorModel {
        return {
            name: "lorryRegNoValidator",
            fn: (value: any) => {
                const relationType = this._relationType.value;
                const cargoType = this._cargoType.value;

                switch (relationType) {
                    case RelationType.ROAD_DEPO_FERRY:
                    case RelationType.FERRY_DEPO_ROAD:
                        if (cargoType === CargoType.NORMATIVE_GENERAL_CARGO || cargoType === CargoType.DANGEROUS_GENERAL_CARGO) {
                            return true;
                        } else {
                            return Validator.isNotEmpty(value);
                        }
                    case RelationType.RAIL_DEPO_FERRY:
                    case RelationType.FERRY_DEPO_RAIL:
                    case RelationType.RAIL_FERRY:
                    case RelationType.FERRY_RAIL:
                    case RelationType.FERRY_DEPO:
                    case RelationType.RAIL_DEPO:
                    case RelationType.RAIL_DEPO_RAIL:
                    case RelationType.RAIL_DEPO_ROAD:
                    case RelationType.ROAD_DEPO_ROAD:
                    case RelationType.ROAD_DEPO_RAIL:
                    case RelationType.ROAD_DEPO:
                        return true;
                    default:
                        return true;
                }
            },
            errorMessage: errorMessages.required
        };
    }

    get supplierLorryRegNoValidator(): ValidatorModel {
        return {
            name: "supplierLorryRegNoValidator",
            fn: (value: any) => {
                const relationType = this._relationType.value;
                const cargoType = this._cargoType.value;

                switch (relationType) {
                    case RelationType.ROAD_DEPO_ROAD:
                    case RelationType.ROAD_DEPO_RAIL:
                    case RelationType.ROAD_DEPO:
                        if (cargoType === CargoType.NORMATIVE_GENERAL_CARGO || cargoType === CargoType.DANGEROUS_GENERAL_CARGO) {
                            return true;
                        } else {
                            return Validator.isNotEmpty(value);
                        }
                    case RelationType.ROAD_DEPO_FERRY:
                    case RelationType.FERRY_DEPO_ROAD:
                    case RelationType.RAIL_DEPO_FERRY:
                    case RelationType.FERRY_DEPO_RAIL:
                    case RelationType.RAIL_FERRY:
                    case RelationType.FERRY_RAIL:
                    case RelationType.FERRY_DEPO:
                    case RelationType.RAIL_DEPO:
                    case RelationType.RAIL_DEPO_RAIL:
                    case RelationType.RAIL_DEPO_ROAD:
                        return true;
                    default:
                        return true;
                }
            },
            errorMessage: errorMessages.required
        };
    }

    get recipientLorryRegNoValidator(): ValidatorModel {
        return {
            name: "recipientLorryRegNoValidator",
            fn: (value: any) => {
                const relationType = this._relationType.value;
                const cargoType = this._cargoType.value;

                switch (relationType) {
                    case RelationType.ROAD_DEPO_ROAD:
                    case RelationType.RAIL_DEPO_ROAD:
                        if (cargoType === CargoType.NORMATIVE_GENERAL_CARGO || cargoType === CargoType.DANGEROUS_GENERAL_CARGO) {
                            return true;
                        } else {
                            return Validator.isNotEmpty(value);
                        }
                    case RelationType.ROAD_DEPO_FERRY:
                    case RelationType.FERRY_DEPO_ROAD:
                    case RelationType.RAIL_DEPO_FERRY:
                    case RelationType.FERRY_DEPO_RAIL:
                    case RelationType.RAIL_FERRY:
                    case RelationType.FERRY_RAIL:
                    case RelationType.FERRY_DEPO:
                    case RelationType.ROAD_DEPO_RAIL:
                    case RelationType.ROAD_DEPO:
                    case RelationType.RAIL_DEPO:
                    case RelationType.RAIL_DEPO_RAIL:
                        return true;
                    default:
                        return true;
                }
            },
            errorMessage: errorMessages.required
        };
    }

    get voyageSectionRequiredValidator(): ValidatorModel {
        return {
            name: "voyageSectionRequiredValidator",
            fn: (value: any) => {
                const relationType = this._relationType.value;

                switch (relationType) {
                    case RelationType.ROAD_DEPO_ROAD:
                    case RelationType.ROAD_DEPO_RAIL:
                    case RelationType.ROAD_DEPO:
                    case RelationType.RAIL_DEPO:
                    case RelationType.RAIL_DEPO_RAIL:
                    case RelationType.RAIL_DEPO_ROAD:
                        return true;
                    case RelationType.ROAD_DEPO_FERRY:
                    case RelationType.FERRY_DEPO_ROAD:
                    case RelationType.RAIL_DEPO_FERRY:
                    case RelationType.FERRY_DEPO_RAIL:
                    case RelationType.FERRY_DEPO:
                    case RelationType.RAIL_FERRY:
                    case RelationType.FERRY_RAIL:
                        return Validator.isNotEmpty(value);
                    default:
                        return true;
                }
            },
            errorMessage: errorMessages.required
        };
    }

    get dateWithTimeValidator(): ValidatorModel {
        return {
            name: "correctDateWithTime",
            fn: (value: any) => {
                const relationType = this._relationType.value;

                switch (relationType) {
                    case RelationType.ROAD_DEPO_ROAD:
                    case RelationType.ROAD_DEPO_RAIL:
                    case RelationType.ROAD_DEPO:
                    case RelationType.RAIL_DEPO:
                    case RelationType.RAIL_DEPO_RAIL:
                    case RelationType.RAIL_DEPO_ROAD:
                        return true;
                    case RelationType.ROAD_DEPO_FERRY:
                    case RelationType.FERRY_DEPO_ROAD:
                    case RelationType.RAIL_DEPO_FERRY:
                    case RelationType.FERRY_DEPO_RAIL:
                    case RelationType.FERRY_DEPO:
                    case RelationType.RAIL_FERRY:
                    case RelationType.FERRY_RAIL:
                        return Validator.isCorrectDateWithTime(value);
                    default:
                        return true;
                }
            },
            errorMessage: () => i18n.t("transportation.ticket.errors.correctDateWithTime")
        };
    }

    get cargoDescValidator(): ValidatorModel {
        return {
            name: "cargoDescValidator",
            fn: (value: any) => {
                const relationType = this._relationType.value;
                const cargoType = this._cargoType.value;

                switch (relationType) {
                    case RelationType.ROAD_DEPO_FERRY:
                    case RelationType.FERRY_DEPO_ROAD:
                    case RelationType.FERRY_DEPO:
                    case RelationType.ROAD_DEPO_ROAD:
                    case RelationType.ROAD_DEPO_RAIL:
                    case RelationType.ROAD_DEPO:
                    case RelationType.RAIL_DEPO:
                    case RelationType.RAIL_DEPO_RAIL:
                    case RelationType.RAIL_DEPO_ROAD:
                        if (cargoType === CargoType.NORMATIVE_GENERAL_CARGO || cargoType === CargoType.DANGEROUS_GENERAL_CARGO) {
                            return Validator.isNotEmpty(value);
                        } else {
                            return true;
                        }
                    case RelationType.RAIL_DEPO_FERRY:
                    case RelationType.FERRY_DEPO_RAIL:
                    case RelationType.RAIL_FERRY:
                    case RelationType.FERRY_RAIL:
                        return true;
                    default:
                        return true;
                }
            },
            errorMessage: errorMessages.required
        };
    }

    // validator for length, height, width
    get cargoDimensionsValidator(): ValidatorModel {
        return {
            name: "cargoDimensionsValidator",
            fn: (value: any) => {
                const relationType = this._relationType.value;
                const cargoType = this._cargoType.value;

                switch (relationType) {
                    case RelationType.ROAD_DEPO_FERRY:
                    case RelationType.FERRY_DEPO_ROAD:
                    case RelationType.FERRY_DEPO:
                    case RelationType.ROAD_DEPO_ROAD:
                    case RelationType.ROAD_DEPO_RAIL:
                    case RelationType.ROAD_DEPO:
                    case RelationType.RAIL_DEPO:
                    case RelationType.RAIL_DEPO_RAIL:
                    case RelationType.RAIL_DEPO_ROAD:
                        if (
                            cargoType === CargoType.OVERSIZE ||
                            cargoType === CargoType.OVERSIZE_TRAILER ||
                            cargoType === CargoType.OVERSIZE_LORRY) {
                            return Validator.isNotEmpty(value);
                        } else {
                            return true;
                        }
                    case RelationType.RAIL_DEPO_FERRY:
                    case RelationType.FERRY_DEPO_RAIL:
                    case RelationType.RAIL_FERRY:
                    case RelationType.FERRY_RAIL:
                        return true;
                    default:
                        return true;
                }
            },
            errorMessage: errorMessages.required
        };
    }

    get recipientValidator(): ValidatorModel {
        return {
            name: "recipientValidator",
            fn: (value: any) => {
                const relationType = this._relationType.value;

                if (relationType === RelationType.FERRY_DEPO_ROAD) {
                    return Validator.isNotEmpty(value);
                } else {
                    return true;
                }
            },
            errorMessage: errorMessages.required
        };
    }

    get wagonRegNoValidator(): ValidatorModel {
        return {
            name: "wagonRegNoValidator",
            fn: (value: any) => {
                const relationType = this._relationType.value;

                if (relationType === RelationType.RAIL_FERRY || relationType === RelationType.FERRY_RAIL) {
                    return Validator.isNotEmpty(value);
                } else {
                    return true;
                }
            },
            errorMessage: errorMessages.required
        };
    }

    // validator for containerNo, containerSize, vgm
    get containerValidator(): ValidatorModel {
        return {
            name: "containerNoValidator",
            fn: (value: any) => {
                const relationType = this._relationType.value;
                const cargoType = this._cargoType.value;

                switch (relationType) {
                    case RelationType.ROAD_DEPO_FERRY:
                    case RelationType.FERRY_DEPO_ROAD:
                    case RelationType.RAIL_DEPO_FERRY:
                    case RelationType.FERRY_DEPO_RAIL:
                    case RelationType.FERRY_DEPO:
                    case RelationType.ROAD_DEPO_ROAD:
                    case RelationType.ROAD_DEPO_RAIL:
                    case RelationType.ROAD_DEPO:
                    case RelationType.RAIL_DEPO:
                    case RelationType.RAIL_DEPO_RAIL:
                    case RelationType.RAIL_DEPO_ROAD:
                        if (cargoType === CargoType.NORMATIVE_CONTAINER || cargoType === CargoType.DANGEROUS_CONTAINER) {
                            return Validator.isNotEmpty(value);
                        } else {
                            return true;
                        }
                    case RelationType.RAIL_FERRY:
                    case RelationType.FERRY_RAIL:
                        return true;
                    default:
                        return true;
                }
            },
            errorMessage: errorMessages.required
        };
    }

    get requireDangerousGoodsValidator(): ValidatorModel {
        const _cargoType = this.cargoType.value ? this.cargoType.value : "";

        return {
            name: "requireDangerousGoodsValidator",
            fn: (value: string) => {
                return (isDangerousGood(_cargoType) && !!value) || !isDangerousGood(_cargoType);
            },
            errorMessage: () => `${i18n.t("errorMessages.required")}`
        };
    }

    get pinCodeValidator(): ValidatorModel {
        const correctPinCodeLength = 5;

        return {
            name: "pinCodeValidator",
            fn: (value: any) => {
                const relationType = this._relationType.value;

                switch (relationType) {
                    case RelationType.FERRY_DEPO_ROAD:
                    case RelationType.ROAD_DEPO_FERRY:
                    case RelationType.ROAD_DEPO_RAIL:
                    case RelationType.RAIL_DEPO_ROAD:
                    case RelationType.ROAD_DEPO_ROAD:
                        return (value.length === 0 || value.length === correctPinCodeLength) && Validator.isOnlyPositiveNumberOrEmpty(value);
                    case RelationType.RAIL_DEPO_FERRY:
                    case RelationType.FERRY_DEPO_RAIL:
                    case RelationType.RAIL_FERRY:
                    case RelationType.FERRY_RAIL:
                    case RelationType.FERRY_DEPO:
                    case RelationType.ROAD_DEPO:
                    case RelationType.RAIL_DEPO:
                    case RelationType.RAIL_DEPO_RAIL:
                        return true;
                    default:
                        return true;
                }
            },
            errorMessage: () => i18n.t("transportation.order.addOrder.errors.pinCodeErrorMessage")
        };
    }

    get displayPinCode() {
        const relationType = this._relationType.value;

        return OrderUtils.displayPinCode(relationType);
    }

    get displayBarCode() {
        const relationType = this._relationType.value;

        return OrderUtils.displayBarCode(relationType);
    }

    get displayLorryRegNo() {
        const relationType = this._relationType.value;

        return OrderUtils.displayLorryRegNo(relationType);
    }

    get displaySupplierLorryRegNo() {
        const relationType = this._relationType.value;

        return OrderUtils.displaySupplierLorryRegNo(relationType);
    }

    get displayRecipientLorryRegNo() {
        const relationType = this._relationType.value;

        return OrderUtils.displayRecipientLorryRegNo(relationType);
    }

    get displayTrailerRegNo() {
        const relationType = this._relationType.value;

        return OrderUtils.displayTrailerRegNo(relationType);
    }

    get displaySecuringDeclaration() {
        const relationType = this._relationType.value;

        return OrderUtils.displaySecuringDeclaration(relationType);
    }

    get displayRecipient() {
        const relationType = this._relationType.value;

        return OrderUtils.displayRecipient(relationType);
    }

    // display packingQuantity, cbm, packingName and remarks
    get displayCargoProperties() {
        const relationType = this._relationType.value;

        return OrderUtils.displayCargoProperties(relationType);
    }

    get displayRecipientWagonRegNo() {
        const relationType = this._relationType.value;

        return OrderUtils.displayRecipientWagonRegNo(relationType);
    }

    get displayWagonRegNo() {
        const relationType = this._relationType.value;

        return OrderUtils.displayWagonRegNo(relationType);
    }

    get displaySupplierWagonRegNo() {
        const relationType = this._relationType.value;

        return OrderUtils.displaySupplierWagonRegNo(relationType);
    }

    // display containerNo, containerSize and vgm
    get displayContainerProperties() {
        const relationType = this._relationType.value;
        const cargoType = this._cargoType.value;

        return OrderUtils.displayContainerProperties(relationType, cargoType);
    }

    get displayTemperatures() {
        const relationType = this._relationType.value;

        return OrderUtils.displayTemperatures(relationType);
    }

    get displayVoyageSection() {
        const relationType = this._relationType.value;

        return OrderUtils.displayVoyageSection(relationType);
    }

    get displayCostsSection() {
        const relationType = this._relationType.value;

        return OrderUtils.displayCostsSection(relationType);
    }

    get companies() {
        return this._companies;
    }

    setCompanies(companies: CompanyModelForList[]) {
        this._companies = companies;
    }

    get statusesForRelationType() {
        return this._statusesForRelationType;
    }

    setStatusesForRelationType(statuses: OrderStatusEnum[]) {
        this._statusesForRelationType = statuses;
    }

    setPaymentMethodForCompany(selectedTransportCompanyId: string) {
        const selectedTransportCompanyData = this.companies.find(x => x.id === selectedTransportCompanyId);

        if (selectedTransportCompanyData?.hasPaymentWithBankTransfer) {
            this.setPaymentMethod(PaymentMethod.BANK_TRANSFER);
        } else {
            this.setPaymentMethod(PaymentMethod.CASH);
        }
    }

    setOrder(orderData: OrderDetailModel | null | undefined) {
        if (!orderData) {
            return;
        }

        this.setStatus(orderData.status);
        this.setRelationType(orderData.relationType);
        this.setCargoType(orderData.cargoType);
        this.setPinCode(orderData.pinCode || "");
        this.setBarcodeNo(orderData.barcodeNo || "");
        this.setRequestedTemp1(orderData.requestedTemp1 || null);
        this.setRequestedTemp2(orderData.requestedTemp2 || null);
        this.setTrailerRegNo(orderData.trailerRegNo || "");
        this.setTransportCompanyId(orderData.transportCompanyId || "");
        this.setRecipient(orderData.recipient || "");
        this.setLorryRegNo(orderData.lorryRegNo || "");
        this.setSupplierLorryRegNo(orderData.supplierLorryRegNo || "");
        this.setRecipientLorryRegNo(orderData.recipientLorryRegNo || "");
        this.setShipownerId(orderData.shipownerId || "");
        this.setVoyageNo(orderData.voyageNo || "");
        this.setArrivalDepartureTime(DateUtils.getDateAndTimeInPolishTimeZone(orderData.arrivalDepartureTime || null));
        this.setFerryId(orderData.ferryId || "");
        this.setOriginDestinationPortId(orderData.originDestinationPortId || "");
        this.setOriginDestinationCountry(orderData.originDestinationCountry || "");
        this.setBookingNo(orderData.bookingNo || "");
        this.setCargoDesc(orderData.cargoDesc || "");
        this.setWeight(orderData.weight);
        this.setLength(orderData.length || null);
        this.setHeight(orderData.height || null);
        this.setWidth(orderData.width || null);
        this.setPackingQuantity(orderData.packingQuantity || null);
        this.setCbm(orderData.cbm || null);
        this.setPackingId(orderData.packingId || "");
        this.setPackingName(orderData.packing || "");
        this.setRemarks(orderData.remarks || "");
        this.setWagonRegNo(orderData.wagonRegNo || "");
        this.setSupplierWagonRegNo(orderData.supplierWagonRegNo || "");
        this.setRecipientWagonRegNo(orderData.recipientWagonRegNo || "");
        this.setSecuringDeclaration(orderData.securingDeclaration || false);
        this.setContainerNo(orderData.containerNo || "");
        this.setContainerSize(orderData.containerSize || "");
        this.setVgm(orderData.vgm || null);
        this.setPaymentMethod(orderData.paymentMethod);
        this.setPaymentDocumentNumber(orderData.paymentDocumentNumber || "");
        this.setStorageTime(orderData.storageTimeInMinutes || null);

        const dangerousGoods = orderData.dangerousGoods;
        const length = dangerousGoods?.length || 0;
        this._dangerousGoods = [];

        if (!length) {
            this.createDangerousGood();
        }

        for (let i = 0; i < length; i++) {
            this.createDangerousGood();
            this.dangerousGoods[i].setPackingId(dangerousGoods![i].packingId || "");
            this.dangerousGoods[i].setPackingGroup(dangerousGoods![i].packingGroup);
            this.dangerousGoods[i].setPackingQuantity(dangerousGoods![i].packingQuantity);
            this.dangerousGoods[i].setWeight(dangerousGoods![i].weight);
            this.dangerousGoods[i].setUnCodeId(dangerousGoods![i].unCodeId || "");
        }
    }
}
